import { Selector } from "@/components/Selector"
import { IOption } from "@/types"
import { cn, moneyFormat } from "@/utils"
import { IBAN, PaymentSystem, useIbanList } from "@usher/pe-client-front-end-lib"
import { ReactNode, useEffect, useMemo } from "react"

interface Props {
  iban?: IBAN
  onChange?: (iban: IOption<string, IBAN>) => void
  autoSelectFirst?: boolean
  paymentSystem?: PaymentSystem
  disabled?: boolean
  error?: string
}

export const IBANSelect = ({ error, autoSelectFirst, disabled, paymentSystem, iban, onChange }: Props) => {
  const { ibanOptions } = useIbanList()
  const filteredIbanOptions = useMemo(() => {
    if (paymentSystem != null)
      return ibanOptions.filter((option) => {
        const meta = option.meta as IBAN
        const currency = paymentSystem === PaymentSystem.SEPA ? "EUR" : "USD"
        return meta.currency === currency
      })
    return ibanOptions
  }, [ibanOptions, paymentSystem])
  const hasMany = (filteredIbanOptions || []).length > 1

  const value = useMemo(() => {
    if (iban != null)
      return filteredIbanOptions.find((option) => {
        const meta = option.meta as IBAN
        return meta.id === iban.id
      })
    if (autoSelectFirst) return filteredIbanOptions[0]
    return ""
  }, [iban, autoSelectFirst, filteredIbanOptions])
  useEffect(() => {
    if (value == null || !iban) onChange?.(value as IOption<string, IBAN>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  return (
    <Selector
      disabled={hasMany ? false : disabled}
      value={value}
      classNames={{ control: "w-full md:w-auto" }}
      variant="contained"
      options={filteredIbanOptions as IOption<string, IBAN>[]}
      onChange={onChange}
      error={error != null}
      getValueLabel={(option) => {
        if (option == null) return ""
        if (typeof option === "string") return option
        const balance = option?.meta?.balance ?? "0"
        return (
          <div className="inline-flex items-center gap-2 text-nowrap flex-wrap">
            <span>
              {option?.label} (Balance:{" "}
              <span
                className={cn({
                  "text-red-600": Number(balance) < 0,
                  "text-green-600": Number(balance) > 0,
                })}
              >
                {moneyFormat(Number(balance), 2)} {option?.meta?.currency}
              </span>
              {") "}
            </span>
          </div>
        )
      }}
      // @ts-ignore
      renderOptions={(option, props, key) => {
        const balance = Number(option?.meta?.balance ?? 0)
        return (
          <div {...props} key={key} className={cn("flex gap-2 items-center", props.className)}>
            <span className="">
              {option?.label} (Balance:{" "}
              <span
                className={cn({
                  "text-red-600": Number(balance) < 0,
                  "text-green-600": Number(balance) > 0,
                })}
              >
                {moneyFormat(Number(balance), 2)} {option?.meta?.currency}
              </span>
              )
            </span>
          </div>
        ) as ReactNode
      }}
    />
  )
}
